import { codeNameLabel } from '@/shared/utils/codeNameLabel';
import { env } from '@/app/config/env';
import { normalizeError } from '@/core/error-handling/ApiErrorHandler';
import { HTTP_STATUS } from '@/app/config/constants';
import { apiClient } from '@/shared/services/api/client';
import { unwrapEnvelope } from '@/shared/services/api/envelope';
import type { ApiEnvelope } from '@/shared/services/api/envelope';
import { delay } from '@/shared/utils/asyncUtils';
import { applyTheme } from '@/shared/hooks/useTheme';
import { MOCK_LATENCY_MS } from '../../common/constants';
import {
  mockCreateSitePreference,
  mockCurrencies,
  mockDateFormats,
  mockGetSitePreference,
  mockTimeFormats,
  mockTimeZones,
  mockUpdateSitePreference,
} from './mocks/siteConfiguration.mock';
import type {
  CurrencyItem,
  DateFormatItem,
  PagedContent,
  ReferenceChoice,
  SitePreferenceRecord,
  SitePreferenceTheme,
  SitePreferenceWritePayload,
  TimeFormatItem,
  TimeZoneItem,
} from '../../common/models';
import { modeFromApi, paletteFromApi } from '../utils/siteConfigurationMapper';
import { dateFormatLabel, timeFormatLabel, timeZoneLabel } from '../utils/referenceLabels';

/**
 * Step 2's API surface, and nothing beyond it -- the seven endpoints in
 * mdfiles/site-config.md section 6.
 *
 * The theme palette/mode lists are NOT here: section 3 is explicit that
 * `/site-preference/theme-options` must not be called, because those are fixed
 * backend enums. They live in ../constants.ts instead.
 */

/** Section 4: every reference list is requested with size=200. */
const REFERENCE_PAGE_SIZE = 200;

const FALLBACK_MESSAGE = 'Server error, please try again';

function sitePreferencePath(organizationCode: string): string {
  return `/organizations/${encodeURIComponent(organizationCode)}/site-preference`;
}

const siteConfigApi = {
  getCurrencies: () =>
    apiClient.get<ApiEnvelope<PagedContent<CurrencyItem>>>('/currencies', {
      params: { size: REFERENCE_PAGE_SIZE },
    }),
  getDateFormats: () =>
    apiClient.get<ApiEnvelope<PagedContent<DateFormatItem>>>('/date-formats', {
      params: { size: REFERENCE_PAGE_SIZE },
    }),
  getTimeFormats: () =>
    apiClient.get<ApiEnvelope<PagedContent<TimeFormatItem>>>('/time-formats', {
      params: { size: REFERENCE_PAGE_SIZE },
    }),
  getTimeZones: () =>
    apiClient.get<ApiEnvelope<PagedContent<TimeZoneItem>>>('/timezones', {
      params: { size: REFERENCE_PAGE_SIZE },
    }),
  getSitePreference: (organizationCode: string) =>
    apiClient.get<ApiEnvelope<SitePreferenceRecord>>(sitePreferencePath(organizationCode)),
  createSitePreference: (organizationCode: string, payload: SitePreferenceWritePayload) =>
    apiClient.post<ApiEnvelope<SitePreferenceRecord>>(
      sitePreferencePath(organizationCode),
      payload,
    ),
  updateSitePreference: (organizationCode: string, payload: SitePreferenceWritePayload) =>
    apiClient.put<ApiEnvelope<SitePreferenceRecord>>(
      sitePreferencePath(organizationCode),
      payload,
    ),
};

function choice(id: number, label: string, isDefault: boolean | undefined): ReferenceChoice {
  return { label, value: String(id), isDefault: isDefault === true };
}

/* -- reference data (section 4) -------------------------------------------- */

/** Section 4.1: the dropdown shows the name; the code disambiguates it. */
export async function getCurrencies(): Promise<ReferenceChoice[]> {
  const items = env.ENABLE_MOCKS
    ? (await delay(MOCK_LATENCY_MS), mockCurrencies())
    : unwrapEnvelope(await siteConfigApi.getCurrencies(), FALLBACK_MESSAGE).content;

  return items.map((item) =>
    choice(item.id, codeNameLabel(item.currencyCode, item.currencyName), item.isDefault),
  );
}

/**
 * Section 4.2. The option reads `DD-MM-YYYY - Day, Month & Four Digit Year`:
 * the platform-composed `label`, see ../utils/referenceLabels.ts. The value is
 * still the row id, so an organization saved before the labels changed keeps
 * its selection.
 */
export async function getDateFormats(): Promise<ReferenceChoice[]> {
  const items = env.ENABLE_MOCKS
    ? (await delay(MOCK_LATENCY_MS), mockDateFormats())
    : unwrapEnvelope(await siteConfigApi.getDateFormats(), FALLBACK_MESSAGE).content;

  return items.map((item) => choice(item.id, dateFormatLabel(item), item.isDefault));
}

/** Section 4.3: `HH:mm:ss - 24-hour format`, same rules as the date format. */
export async function getTimeFormats(): Promise<ReferenceChoice[]> {
  const items = env.ENABLE_MOCKS
    ? (await delay(MOCK_LATENCY_MS), mockTimeFormats())
    : unwrapEnvelope(await siteConfigApi.getTimeFormats(), FALLBACK_MESSAGE).content;

  return items.map((item) => choice(item.id, timeFormatLabel(item), item.isDefault));
}

/**
 * Section 4.4: `IST (+5:30)` -- abbreviation and standard UTC offset, derived
 * by the platform from the IANA identifier. Never the raw identifier.
 */
export async function getTimeZones(): Promise<ReferenceChoice[]> {
  const items = env.ENABLE_MOCKS
    ? (await delay(MOCK_LATENCY_MS), mockTimeZones())
    : unwrapEnvelope(await siteConfigApi.getTimeZones(), FALLBACK_MESSAGE).content;

  return items.map((item) => choice(item.id, timeZoneLabel(item), item.isDefault));
}

/* -- site preference (section 5) ------------------------------------------- */

/**
 * Section 5.4. Returns null on 404, which is NOT an error here: it means step
 * 2 has not been saved yet. Every other failure still throws.
 *
 * That null is also the POST-vs-PUT signal — see `saveSiteConfiguration`.
 */
export interface OrganizationLoginImageResponse {
  organizationCode: string | null;
  loginImage: string | null;
  orgImage?: string | null;
  organizationName?: string | null;
  /**
   * The `osp_configurations` jsonb document, absent only from an older backend. Its
   * `theme` block is what makes the organization's theme follow the user across
   * browsers -- see `applyOrganizationTheme`.
   */
  configurations?: { theme?: SitePreferenceTheme | null } | null;
}

export async function getOrganizationLoginImage(
  organizationCode?: string,
): Promise<OrganizationLoginImageResponse> {
  try {
    const res = await apiClient.get<ApiEnvelope<OrganizationLoginImageResponse>>(
      '/organizationloginimage',
      {
        params: organizationCode ? { organizationCode } : undefined,
      },
    );
    return unwrapEnvelope(res, FALLBACK_MESSAGE);
  } catch {
    return {
      organizationCode: organizationCode ?? null,
      loginImage: null,
      orgImage: null,
      organizationName: null,
      configurations: null,
    };
  }
}

/**
 * Stores the theme `/organizationloginimage` carries and paints it onto <html>
 * in one call -- the same `applyTheme` the Customize panel uses, so the login
 * page, the setup wizard and the next page load all read one stored value.
 *
 * Nothing to apply is not an error: an organization that never saved a site
 * configuration leaves whatever is already on the document alone rather than
 * yanking the user back to the default palette. The wire value is a label
 * ("Rose"), so `paletteFromApi` / `modeFromApi` do the label-to-slug translation
 * the wizard's mapper already owns.
 *
 * @returns true when a theme was found and applied.
 */
export function applyOrganizationTheme(
  response: Pick<OrganizationLoginImageResponse, 'configurations'> | null | undefined,
): boolean {
  const theme = response?.configurations?.theme;
  if (!theme) return false;

  applyTheme({ palette: paletteFromApi(theme.palette), mode: modeFromApi(theme.mode) });
  return true;
}

export async function getSitePreference(
  organizationCode: string,
): Promise<SitePreferenceRecord | null> {
  try {
    if (env.ENABLE_MOCKS) {
      await delay(MOCK_LATENCY_MS);
      return mockGetSitePreference(organizationCode);
    }
    return unwrapEnvelope(await siteConfigApi.getSitePreference(organizationCode), FALLBACK_MESSAGE);
  } catch (error: unknown) {
    if (normalizeError(error).status === HTTP_STATUS.NOT_FOUND) return null;
    throw error;
  }
}

export async function createSitePreference(
  organizationCode: string,
  payload: SitePreferenceWritePayload,
): Promise<SitePreferenceRecord> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return mockCreateSitePreference(organizationCode, payload);
  }
  return unwrapEnvelope(
    await siteConfigApi.createSitePreference(organizationCode, payload),
    FALLBACK_MESSAGE,
  );
}

/** Full replacement -- an omitted field is cleared, not preserved (5.2). */
export async function updateSitePreference(
  organizationCode: string,
  payload: SitePreferenceWritePayload,
): Promise<SitePreferenceRecord> {
  if (env.ENABLE_MOCKS) {
    await delay(MOCK_LATENCY_MS);
    return mockUpdateSitePreference(organizationCode, payload);
  }
  return unwrapEnvelope(
    await siteConfigApi.updateSitePreference(organizationCode, payload),
    FALLBACK_MESSAGE,
  );
}

/**
 * Save & Next, section 5's preamble.
 *
 * The verb is decided by asking the server, not by a local "already saved"
 * flag: GET first, then PUT on 200 and POST on 404. Section 5.4 requires this
 * on EVERY save, so it stays correct even when the row was created or removed
 * outside this wizard session.
 */
export async function saveSiteConfiguration(
  organizationCode: string,
  payload: SitePreferenceWritePayload,
): Promise<SitePreferenceRecord> {
  const existing = await getSitePreference(organizationCode);

  return existing
    ? updateSitePreference(organizationCode, payload)
    : createSitePreference(organizationCode, payload);
}
